<script setup lang="ts">
import { type IJournal } from "~/types";
import type { IRTSidepanelParticipatingJournals } from "./IRTSidepanelParticipatingJournals";
import {
  CardJournal,
  type ICardJournalProps
} from "@frontiers-vue3/public-pages";
import OtherParticipatingJournals from "./OtherParticipatingJournals/OtherParticipatingJournals.vue";
import { WindowSizesEnum } from "~/enums";
import type {
  IPopoverModifierPosition,
  popoverBindingValue
} from "@frontiers-vue3/public-pages/dist/common/directives";
import { getBynderImageUrl } from "~/helpers";
import type { IBynderImage } from "~/types/contentful/IBynderImage";

const props = withDefaults(defineProps<IRTSidepanelParticipatingJournals>(), {
  journals: () => []
});

const windowSize: Ref<WindowSizesEnum> = useGetWindowSize();

const sortedJournalsAndSections = computed((): IJournal[] => {
  const sortedJournals = [...props.journals];
  return sortedJournals
    .sort((j1, j2) => {
      j1.sections?.sort((s1, s2) => s1.title.localeCompare(s2.title));
      return j1.title.localeCompare(j2.title);
    })
    .filter((j) => !j.isPrimary);
});

const primaryJournalCardProps = computed((): ICardJournalProps | undefined => {
  const primaryJournal = props.journals.find((j) => j.isPrimary);
  if (!primaryJournal) return undefined;

  const contentfulJournal = useMain().primaryJournal;
  let primaryJournalImageSource = [""];
  if (contentfulJournal?.banner) {
    primaryJournalImageSource = [
      getBynderImageUrl(
        contentfulJournal.banner[0] as IBynderImage,
        "WebsiteWebP_M"
      )
    ];
  }

  const enablePopover =
    !primaryJournal.isOnline &&
    !primaryJournal.sections?.find((s) => s.isOnline);

  return {
    journal: {
      id: primaryJournal.id,
      slug: primaryJournal.slug,
      image: {
        sources: primaryJournalImageSource,
        imageAlt: primaryJournal.title
      },
      name: primaryJournal.title,
      impactFactor: primaryJournal.impactMetrics.impactFactor,
      citeScore: primaryJournal.impactMetrics.citeScore,
      isOnline: primaryJournal.isOnline
    },
    sections: primaryJournal.sections?.map((s) => {
      return {
        slug: s.slug,
        name: s.title,
        id: s.sectionId,
        isOnline: s.isOnline
      };
    }),
    popover: enablePopover && popoverConfig.value
  };
});

const popoverConfig = computed(() => {
  const position = getPosition(windowSize.value);
  const config: popoverBindingValue = {
    message: "This journal is not currently available.",
    position
  };
  return config;
});

const getPosition = (windowSize: WindowSizesEnum): IPopoverModifierPosition => {
  switch (windowSize) {
    case WindowSizesEnum.MOBILE:
    case WindowSizesEnum.TABLET_PORTRAIT:
    case WindowSizesEnum.TABLET_LANDSCAPE:
      return "topleft";
    default:
      return "leftcenter";
  }
};

const updatePosition = () => {
  popoverConfig.value.position = getPosition(useGetWindowSize().value);
};

onMounted(() => {
  updatePosition();
  window.addEventListener("resize", updatePosition);
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", updatePosition);
});
</script>
<template>
  <div class="RtSidePanel__Section">
    <CardJournal
      v-if="primaryJournalCardProps"
      :key="`${primaryJournalCardProps.journal.id}-${popoverConfig.position}`"
      heading="h2"
      section-heading="h3"
      v-bind="primaryJournalCardProps" />
    <OtherParticipatingJournals
      :sorted-journals-and-sections="sortedJournalsAndSections"
      :popover-config="popoverConfig" />
  </div>
</template>
<style lang="scss">
.RtSidePanel__Section {
  border-bottom: 0;

  .CardJournal {
    margin: -16px;
    padding: 16px;
    width: auto;

    &:hover {
      box-shadow: none;
    }

    &--disabled {
      margin: 0;
      padding: 0;

      @media #{$break-md} {
        padding: 16px;
      }
    }

    @media #{$break-md} {
      background-color: white;
      margin: 0 0 16px;
    }

    @media #{$break-xs} {
      width: auto;
    }

    &__Body {
      @media #{$break-md} {
        background-color: white;
      }
    }

    &__Footer {
      @media #{$break-md} {
        background-color: white;
      }
    }

    .CardJournal__ImgWrapper {
      background-color: var(--maincolor50);

      @media #{$break-md} {
        margin-left: 6px;
      }

      .FrontiersImage {
        &--error {
          .is-inside-mask {
            display: none;
          }
        }

        .is-inside-mask {
          &[src=""] {
            display: none;
          }

          path {
            fill: var(--maincolor50);
          }
        }
      }
    }

    &__Link {
      width: 100%;
    }
  }
}
.Popover {
  @media #{$break-xs} {
    width: calc(100vw - 34px);
    box-sizing: border-box;
  }

  .Popover--left.Popover--acenter {
    max-width: 260px;
  }
}
</style>
